import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuthStore } from '~/store/auth';

/**
 * F94 пака `tails-closeout` (Ф6, SOLUTION §9.5; решение владельца В-37 «Один — Алматы») —
 * деловой день на фронте.
 *
 * «Сегодня» модуль задач считал тремя способами: локальным днём браузера (`localTodayIso`),
 * днём UTC (`toISOString().slice(0, 10)`) и сравнением `Date` с локальной полуночью. Срок
 * «вчера по Алматы» у сотрудника в другом поясе был то просрочен, то нет — а сервер (B76)
 * считает деловым поясом. Пояс приходит в `/auth/me` (`business_timezone`), день — через
 * `Intl`; старый бэкенд без поля — `Asia/Almaty`, как `STAITER_SCHEDULER_TZ` по умолчанию.
 */

const DEFAULT_TZ = 'Asia/Almaty';
const DAY_MS = 24 * 60 * 60 * 1000;

/** Деловой пояс из `/auth/me`. */
export function businessTimeZone(): string {
  return useAuthStore.getState().user?.business_timezone || DEFAULT_TZ;
}

/** Дата `YYYY-MM-DD` в поясе `tz` на момент `at`. */
export function isoDateInZone(at: Date, tz: string): string {
  // Локаль не задаём: части собираем по типу, порядок локали не важен; цифры — латинские.
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    numberingSystem: 'latn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Сегодня делового пояса. */
export function businessTodayIso(at: Date = new Date(), tz: string = businessTimeZone()): string {
  return isoDateInZone(at, tz);
}

/** `iso` + `days` календарных дней (без поясов: арифметика над датой). */
export function addIsoDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Календарных дней от `from` до `to` (`to` − `from`). */
export function isoDaysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / DAY_MS,
  );
}

/**
 * Миллисекунд до ближайшей деловой полуночи — бинарным поиском момента, когда дата в поясе
 * сменится (смещение пояса не зашиваем: переход на летнее время расчёт не ломает).
 */
export function msUntilBusinessMidnight(
  at: Date = new Date(),
  tz: string = businessTimeZone(),
): number {
  const today = isoDateInZone(at, tz);
  let lo = 0;
  let hi = DAY_MS + 60 * 60 * 1000;
  while (hi - lo > 1000) {
    const mid = Math.floor((lo + hi) / 2);
    if (isoDateInZone(new Date(at.getTime() + mid), tz) === today) lo = mid;
    else hi = mid;
  }
  return hi;
}

/** Ключи запросов, в которых живут «просрочено / сегодня / скоро». */
const DAY_SENSITIVE_KEYS = ['tasks', 'projects', 'my-day', 'task-module', 'notifications'] as const;

/**
 * Запросы счётчиков и списков устаревают в деловую полночь (§9.5, К3-28): без таймера чип
 * «просрочено» не переключится до ближайшего события, и вкладка, открытая с вечера, утром
 * покажет вчерашнее «сегодня».
 */
export function useBusinessMidnightInvalidation(): void {
  const qc = useQueryClient();
  const tz = useAuthStore((s) => s.user?.business_timezone || DEFAULT_TZ);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      // +1 с — чтобы `businessTodayIso()` в перерисовке уже вернул новый день.
      timer = setTimeout(() => {
        for (const key of DAY_SENSITIVE_KEYS) void qc.invalidateQueries({ queryKey: [key] });
        arm();
      }, msUntilBusinessMidnight(new Date(), tz) + 1000);
    };
    arm();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [qc, tz]);
}

/**
 * С-G Р.7 — деловой день как состояние: сменяется в деловую полночь и перерисовывает
 * потребителя. Мемо отборов и группировок, которые считают «сегодня» через
 * `businessTodayIso()` внутри, без этого держали вчерашний день во вкладке, открытой ночь:
 * запросы обновлялись по таймеру, а мемо — нет (его зависимости не менялись).
 */
export function useBusinessToday(): string {
  const tz = useAuthStore((s) => s.user?.business_timezone || DEFAULT_TZ);
  const [today, setToday] = useState(() => businessTodayIso(new Date(), tz));
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      timer = setTimeout(() => {
        setToday(businessTodayIso(new Date(), tz));
        arm();
      }, msUntilBusinessMidnight(new Date(), tz) + 1000);
    };
    setToday(businessTodayIso(new Date(), tz));
    arm();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [tz]);
  return today;
}
